/**
 * Move to Folder Dialog
 *
 * Dialog for moving a workspace item to a folder.
 * Part of the Data Workspace OS feature set.
 */

import { useState } from 'react';
import { Folder, FolderOpen, Home, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ScrollArea,
  cn,
} from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useFoldersQuery, useMoveToFolderMutation } from '@/features/organization/hooks';
import { toast } from '@/lib/toast';
import { useUndoStore } from '@/store/useUndoStore';

// ============================================================================
// TYPES
// ============================================================================

interface MoveItem {
  id: string;
  type: WorkspaceItemType;
}

interface MoveToFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Items to move - can be single or multiple */
  items: MoveItem[];
  currentFolderId?: string | null;
  onMoveComplete?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function MoveToFolderDialog({
  isOpen,
  onClose,
  items,
  currentFolderId,
  onMoveComplete,
}: MoveToFolderDialogProps) {
  const { data: folders, isLoading } = useFoldersQuery();
  const moveToFolderMutation = useMoveToFolderMutation();
  const { setAction: setUndoAction } = useUndoStore();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const isBulkMove = items.length > 1;

  const handleMove = async () => {
    if (items.length === 0) return;

    try {
      // Store the original folder ID for undo (simplified: all items assumed to be in same folder)
      const originalFolderId = currentFolderId ?? null;

      // Move all items
      for (const item of items) {
        await moveToFolderMutation.mutateAsync({
          itemType: item.type,
          itemId: item.id,
          folderId: selectedFolderId,
        });
      }

      // Get folder name for toast message
      const targetFolderName = selectedFolderId
        ? folders?.find((f) => f.id === selectedFolderId)?.name ?? 'folder'
        : 'Root';

      const moveMessage = isBulkMove
        ? `${items.length} items moved to ${targetFolderName}`
        : `Moved to ${targetFolderName}`;

      // Set up undo action
      setUndoAction({
        description: moveMessage,
        undoFn: async () => {
          for (const item of items) {
            await moveToFolderMutation.mutateAsync({
              itemType: item.type,
              itemId: item.id,
              folderId: originalFolderId,
            });
          }
          // No toast - the undo action itself is confirmation enough
        },
      });

      // Show toast with undo button (no separate toast on undo - action is confirmation)
      toast.successWithUndo(moveMessage, async () => {
        try {
          for (const item of items) {
            await moveToFolderMutation.mutateAsync({
              itemType: item.type,
              itemId: item.id,
              folderId: originalFolderId,
            });
          }
          // No toast - the undo action itself is confirmation enough
        } catch (err) {
          toast.workspace.undoMoveFailed(err instanceof Error ? err.message : undefined);
        }
      });

      onMoveComplete?.();
      onClose();
    } catch (err) {
      toast.workspace.moveFailed(err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move to Folder</DialogTitle>
          <DialogDescription>
            {isBulkMove
              ? `Select a folder to move ${items.length} items to, or choose root to remove from folders.`
              : 'Select a folder to move this item to, or choose root to remove from folders.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[300px] pr-4">
          <div className="space-y-1">
            {/* Root option */}
            <button
              type="button"
              onClick={() => setSelectedFolderId(null)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                selectedFolderId === null
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              )}
            >
              <Home className="size-4" />
              <span>Root (No folder)</span>
            </button>

            {/* Loading state */}
            {isLoading && (
              <div className="text-muted-foreground py-4 text-center text-sm">
                Loading folders...
              </div>
            )}

            {/* Folders list */}
            {folders?.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => setSelectedFolderId(folder.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  selectedFolderId === folder.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                )}
              >
                {selectedFolderId === folder.id ? (
                  <FolderOpen className="size-4 fill-amber-500 text-amber-600" />
                ) : (
                  <Folder className="size-4 fill-amber-500 text-amber-600" />
                )}
                <span className="flex-1 truncate">{folder.name}</span>
                {(folder.table_count > 0 || folder.linked_table_count > 0) && (
                  <span className="text-muted-foreground text-xs">
                    {folder.table_count + folder.linked_table_count} items
                  </span>
                )}
              </button>
            ))}

            {/* Empty state */}
            {!isLoading && folders?.length === 0 && (
              <div className="text-muted-foreground py-4 text-center text-sm">
                No folders yet. Create a folder first.
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={moveToFolderMutation.isPending}>
            {moveToFolderMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Moving...
              </>
            ) : (
              'Move'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
